import { useEffect, useRef } from "react";
import { SECTION_IDS, SECTIONS } from "../../lib/sections";

const HALL = SECTION_IDS.map((id) => SECTIONS[id].tint);
const CALM = ["#E4D2F0", "#F2D8E6", "#D9CDF1", "#F6E3EE", "#E9DDF5"];
const PALE = "#EFE3F1";
const rand = (a, b) => a + Math.random() * (b - a);
export const VEIL_MS = { burst: 1500, rise: 2400 };

function bubbles(mode, W, H) {
  const n = mode === "burst" ? 16 : 70;
  return Array.from({ length: n }, (_, i) => ({
    x: rand(0.05, 0.95) * W,
    y: mode === "burst" ? rand(0.1, 0.9) * H : H + rand(0, H * 0.6),
    r: mode === "burst" ? rand(28, 70) : rand(14, 60),
    c: mode === "rise" ? CALM[i % CALM.length] : HALL[i % HALL.length],
    at: mode === "burst" ? rand(0.35, 0.75) : 0,
    v: rand(0.7, 1.3),
  }));
}

function ring(g, b, r0, a) {
  const r = Math.max(1, r0);
  g.globalAlpha = Math.max(0, Math.min(1, a));
  const grd = g.createRadialGradient(b.x - r * 0.3, b.y - r * 0.3, r * 0.1, b.x, b.y, r);
  grd.addColorStop(0, "rgba(255,255,255,0.55)");
  grd.addColorStop(0.75, "rgba(255,255,255,0.05)");
  grd.addColorStop(1, b.c);
  g.fillStyle = grd;
  g.beginPath();
  g.arc(b.x, b.y, r, 0, Math.PI * 2);
  g.fill();
}

function drawBurst(g, list, k, W, H) {
  const wash = Math.min(1, Math.max(0, (k - 0.3) / 0.45));
  list.forEach((b) => {
    const t = (k - b.at) / 0.35;
    if (t < 0) return ring(g, b, b.r * (1 + k * 2.2), 0.9);
    g.globalAlpha = Math.max(0, 0.75 * (1 - t * 0.4));
    g.fillStyle = b.c;
    g.beginPath();
    g.arc(b.x, b.y, b.r * (3 + t * 9), 0, Math.PI * 2);
    g.fill();
  });
  g.globalAlpha = wash * 0.55;
  g.fillStyle = HALL[0];
  g.fillRect(0, 0, W, H);
}

function drawRise(g, list, k, W, H) {
  g.globalAlpha = Math.min(1, k * 1.6);
  g.fillStyle = PALE;
  g.fillRect(0, 0, W, H);
  list.forEach((b) => {
    b.y -= H * 0.022 * b.v;
    ring(g, b, b.r * (0.7 + k * 0.6), Math.min(1, k * 3) * 0.85);
  });
}

export function BubbleVeil({ mode, onMidpoint, onDone }) {
  const ref = useRef(null);
  const cbs = useRef({ onMidpoint, onDone });
  cbs.current = { onMidpoint, onDone };
  useEffect(() => {
    const c = ref.current;
    const W = (c.width = window.innerWidth);
    const H = (c.height = window.innerHeight);
    const g = c.getContext("2d");
    const list = bubbles(mode, W, H);
    const total = VEIL_MS[mode];
    const mid = mode === "rise" ? 1300 : 650;
    const start = performance.now();
    let raf, midDone = false;
    const frame = (now) => {
      const el = Math.max(0, now - start);
      if (!midDone && el >= mid) {
        midDone = true;
        cbs.current.onMidpoint?.();
      }
      if (el >= total) return cbs.current.onDone?.();
      const k = Math.min(1, el / mid);
      g.clearRect(0, 0, W, H);
      c.style.opacity = el > mid ? String(Math.max(0, 1 - (el - mid) / (total - mid))) : "1";
      (mode === "burst" ? drawBurst : drawRise)(g, list, k, W, H);
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [mode]);
  return <canvas ref={ref} data-testid="bubble-veil" data-mode={mode} className="pointer-events-none fixed inset-0 z-[85] h-full w-full" />;
}
